import { useState } from "react";
import { useParams } from "react-router-dom";

import styled from "styled-components/macro";

import { useGiveLogout } from "Login/TwoFA/API/queries";
import { usePaymentPage } from "Payments/PaymentPage/PaymentPageContext";
import { motion } from "framer-motion";

import { formatPhoneNumber } from "Utilities/Number/formatPhoneNumber";

import { usePaymentForm } from "../PaymentFormContext";

import { useResetClientAuthState } from "./stateHooks";

export default function CurrentUser() {
  const {
    state: {
      user: { name, email, phoneNumber },
      tabID,
      authCodeData: { loginValue },
      accountIsPending,
    },
  } = usePaymentPage();
  const { fundID } = useParams();
  const { state, dispatch: formDispatch } = usePaymentForm();
  const { isSubmitting } = state;
  const authSkipped = state.formStages.auth === "skipped";

  const { mutate: startOver } = useGiveLogout({
    fundID: Number(fundID),
    tabID,
  });

  // if user does not have authCodeData.loginValue, we can assume their info was populated via initial window.data
  const hadInitialUserData = !loginValue;

  const [rememberName] = useState(name);
  const [rememberEmail] = useState(email);
  const [rememberPhone] = useState(phoneNumber);

  const handleLoginClick = () => {
    if (isSubmitting) return;

    formDispatch({
      type: "setFormStages",
      payload: {
        auth: "requestCode",
        paymentInfo: "untouched",
      },
    });
  };

  const resetClientAuthState = useResetClientAuthState();
  const handleNotYouClick = () => {
    if (isSubmitting) return;

    startOver();
    resetClientAuthState();
  };

  const renderUserInfo = () => {
    if (authSkipped) {
      return <Name>{loginValue}</Name>;
    } else {
      return (
        <>
          {rememberName && (
            <Name data-cy="fund-it-user-name">{rememberName}</Name>
          )}
          {(!rememberName || !rememberEmail) && rememberPhone && (
            <Phone>{formatPhoneNumber(rememberPhone)}</Phone>
          )}
          {rememberEmail && (
            <Email data-cy="fund-it-user-email">{rememberEmail}</Email>
          )}
        </>
      );
    }
  };

  return (
    <Container
      initial={!hadInitialUserData && { height: 0 }}
      animate={{ height: "auto" }}
      exit={{ height: 0 }}
    >
      <Wrapper
        initial={!hadInitialUserData && { scale: 0.6, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1, transition: { delay: 0.2 } }}
        exit={{
          y: "-25%",
          scale: 0.8,
          opacity: 0,
          transition: { type: "tween" },
        }}
      >
        <User data-cy="paymentPageRenderUserInfo">{renderUserInfo()}</User>

        <NotYouSection>
          {authSkipped && !accountIsPending ? (
            <Link
              disabled={isSubmitting}
              whileTap={{ scale: isSubmitting ? 1 : 0.85 }}
              onClick={handleLoginClick}
            >
              Log in
            </Link>
          ) : (
            <Link
              disabled={isSubmitting}
              whileTap={{ scale: isSubmitting ? 1 : 0.85 }}
              onClick={handleNotYouClick}
            >
              Not you?
            </Link>
          )}
        </NotYouSection>
      </Wrapper>
    </Container>
  );
}

const Container = styled(motion.div)`
  height: auto;
`;
const Wrapper = styled(motion.div)`
  padding: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  white-space: nowrap;
  flex-wrap: wrap;
`;

const User = styled.div`
  margin-right: 14px;
  flex-shrink: 0;
`;

const Name = styled.div`
  font-size: 20px;
  font-weight: 400;
`;

const Link = styled(motion.div)`
  color: #656666;
  flex-shrink: 0;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: color 0.2s;

  &:hover {
    color: ${({ disabled }) => (disabled ? null : "var(--fundColor)")};
  }
`;

const Email = styled.div`
  font-size: 12px;
  margin-top: 2px;
`;
const Phone = styled.div`
  margin-top: 2px;
`;

const NotYouSection = styled.div`
  @media screen and (max-width: 480px) {
    margin-top: 8px;
    font-size: 14px;
    width: 100%;
  }
`;
